import { BaseModelWithFiles, WithExtraFileBuckets } from "@/models/base-with-files.model.ts"
import {
  type IIncomingInvoice,
  type IInvoiceAnalysisFeedback
} from "@/interfaces/api/incoming-invoice.interface.ts"
import { KnownBusinessEntity } from "@/models/known-business-entity.model.ts"
import type { Moment } from "moment"
import { ModelFactory } from "@/factories/model-factory.ts"
import { IncomingInvoicePurchase } from "@/models/incoming-invoice-purchase.model.ts"
import { IncomingInvoiceRentalIntakeMaterialization } from "@/models/incoming-invoice-rental-intake-materialization.model.ts"
import moment from "moment/moment"
import { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"

export class IncomingInvoice
  extends WithExtraFileBuckets("fileBucketAttachments")(BaseModelWithFiles)
  implements IIncomingInvoice
{
  public static className = "IncomingInvoice"
  public static verboseName = "Inkoopfactuur"

  supplierName: string
  matchedAmountSum: string

  supplier: KnownBusinessEntity | null
  processedBy: KnownBusinessEntityContact | null
  reviewedBy: KnownBusinessEntityContact | null
  rejectedBy: KnownBusinessEntityContact | null
  deletedBy: KnownBusinessEntityContact | null
  incomingInvoicePurchases: IncomingInvoicePurchase[]
  incomingInvoiceRentalIntakeMaterializations: IncomingInvoiceRentalIntakeMaterialization[]
  reference?: string
  date: Moment
  dueDate: Moment
  amountExclVat: string
  comment: string
  processedAt: Moment | null
  reviewedAt: Moment | null
  rejectedAt: Moment | null
  deletedAt: Moment | null
  exportedToAccountingAt: Moment | null
  analysisFeedback: IInvoiceAnalysisFeedback[]

  constructor(incomingInvoice: IIncomingInvoice, managed = false) {
    super(incomingInvoice, managed)

    this.supplierName = incomingInvoice.supplierName

    this.supplier = ModelFactory.childModel(KnownBusinessEntity, incomingInvoice.supplier, managed)
    this.matchedAmountSum = incomingInvoice.matchedAmountSum

    this.incomingInvoicePurchases = ModelFactory.childModels(
      IncomingInvoicePurchase,
      incomingInvoice.incomingInvoicePurchases,
      managed
    )
    this.incomingInvoiceRentalIntakeMaterializations = ModelFactory.childModels(
      IncomingInvoiceRentalIntakeMaterialization,
      incomingInvoice.incomingInvoiceRentalIntakeMaterializations,
      managed
    )
    this.processedBy = ModelFactory.childModel(
      KnownBusinessEntityContact,
      incomingInvoice.processedBy,
      managed
    )
    this.reviewedBy = ModelFactory.childModel(
      KnownBusinessEntityContact,
      incomingInvoice.reviewedBy,
      managed
    )
    this.rejectedBy = ModelFactory.childModel(
      KnownBusinessEntityContact,
      incomingInvoice.rejectedBy,
      managed
    )
    this.deletedBy = ModelFactory.childModel(
      KnownBusinessEntityContact,
      incomingInvoice.deletedBy,
      managed
    )
    this.reference = incomingInvoice.reference
    this.date = incomingInvoice.date
    this.dueDate = incomingInvoice.dueDate
    this.amountExclVat = incomingInvoice.amountExclVat
    this.comment = incomingInvoice.comment
    this.processedAt = incomingInvoice.processedAt
    this.reviewedAt = incomingInvoice.reviewedAt
    this.rejectedAt = incomingInvoice.rejectedAt
    this.deletedAt = incomingInvoice.deletedAt
    this.exportedToAccountingAt = incomingInvoice.exportedToAccountingAt
    this.analysisFeedback = Array.isArray(incomingInvoice.analysisFeedback)
      ? incomingInvoice.analysisFeedback
      : []
  }

  /**
   * Returns the name of the supplier.
   * If the supplier name is not set, the supplier name is returned.
   *
   * @return the name of the supplier
   */
  public getSupplierName() {
    return this.supplier?.name || this.supplierName
  }

  /**
   * Checks whether this model is overdue.
   * An invoice is overdue if the due date is in the past.
   *
   * @return true if the due date is in the past
   */
  public isOverdue() {
    if (!this.dueDate) {
      return false
    }
    const today = moment().startOf("day")
    return this.dueDate.isBefore(today)
  }

  /**
   * Checks whether this model appears to have linked events.
   *
   * If the model is shallow, the result is based only on the
   * locally cached aggregate 'matchedAmountSum'. This may be stale compared to
   * the server:
   * - Might return false even if linked events exist on the server.
   * - Might return true even if those events were removed.
   *
   * If the model is not shallow, the check uses the synchronized collections
   * (incomingInvoicePurchases, incomingInvoiceRentalIntakeMaterializations)
   * and is therefore accurate with respect to the current client state.
   */
  public hasLinkedEvents() {
    if (this.isShallow()) {
      return parseFloat(this.matchedAmountSum) !== 0
    } else {
      return (
        this.incomingInvoicePurchases.length > 0 ||
        this.incomingInvoiceRentalIntakeMaterializations.length > 0
      )
    }
  }
}
